import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  Facebook,
  Globe,
  Instagram,
  Music2,
  Play,
  Youtube,
} from 'lucide-react';
import { AmbassadorApplicationForm } from '@/components/AmbassadorApplicationForm';
import { WARRIORS, WARRIOR_TOP_BG } from '@/data/warriors';

const MISSION = '#1A5276';
const TEAL = '#148F77';
const GOLD = '#D4AC0D';

const WARRIOR_ACCENT: Record<'navy' | 'teal' | 'gold' | 'default', string> = {
  navy: MISSION,
  teal: TEAL,
  gold: GOLD,
  default: MISSION,
};

export function WarriorsPage(): JSX.Element {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== '#apply') return;
    const applySection = document.getElementById('apply');
    if (!applySection) return;
    requestAnimationFrame(() => {
      applySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  const totalVideos = WARRIORS.reduce((sum, warrior) => sum + warrior.stats.videos, 0);
  const maxLanguages = Math.max(...WARRIORS.map((warrior) => warrior.stats.languages));
  const maxCountries = Math.max(...WARRIORS.map((warrior) => warrior.stats.countries));
  const estimatedReach = totalVideos * 2800;

  return (
    <main>
      <section className="bg-white py-14 sm:py-20 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p
            className="text-xs font-bold tracking-[0.38em] uppercase"
            style={{ color: MISSION }}
          >
            CKD Warrior Ambassadors
          </p>
          <h1 className="mt-3 text-3xl sm:text-5xl font-bold text-[#1C2833]">
            Voices on the front line of CKD support.
          </h1>
          <p className="mt-4 max-w-3xl text-base sm:text-lg text-slate-600">
            Our ambassadors are patients, survivors, and caregivers using their stories
            to drive treatment continuity and community action across the Philippines.
          </p>
          <Link
            to="#apply"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="mt-6 inline-flex items-center gap-2 rounded-lg border-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors hover:bg-[#148F77] hover:text-white"
            style={{ borderColor: TEAL, color: TEAL }}
          >
            Apply to become a warrior
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="bg-[#F9FAFB] py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {WARRIORS.map((warrior) => {
              const accentColor = WARRIOR_ACCENT[warrior.accent];
              const topBg = WARRIOR_TOP_BG[warrior.accent];
              return (
                <article
                  key={warrior.slug}
                  className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-[0_12px_26px_rgba(15,23,42,0.07)]"
                >
                  <div
                    className="relative h-52 sm:h-56"
                    style={{
                      background: topBg,
                    }}
                  >
                    <span
                      className="absolute left-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm"
                      aria-hidden="true"
                    >
                      <Play size={16} fill="currentColor" />
                    </span>
                    <span
                      className="absolute bottom-4 right-5 text-7xl font-black uppercase leading-none"
                      style={{ color: `${MISSION}2E` }}
                      aria-hidden="true"
                    >
                      {warrior.name.charAt(0)}
                    </span>
                  </div>
                  <div className="p-5 sm:p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: accentColor }}>
                      {warrior.role}
                    </p>
                    <h2 className="mt-2 text-2xl font-bold text-[#20485F]">{warrior.name}</h2>
                    <p
                      className="mt-3 text-[1.02rem] leading-relaxed italic"
                      style={{ color: '#304A5A', fontFamily: 'Georgia, "Times New Roman", serif' }}
                    >
                      “{warrior.hook}”
                    </p>
                    <p className="mt-3 text-sm text-slate-600">{warrior.headline}</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {warrior.storySnippet}
                    </p>

                    <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-slate-50 p-2 text-center">
                        <p className="font-semibold text-slate-700">{warrior.stats.videos}</p>
                        <p className="text-slate-500">Videos</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2 text-center">
                        <p className="font-semibold text-slate-700">{warrior.stats.languages}</p>
                        <p className="text-slate-500">Languages</p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2 text-center">
                        <p className="font-semibold text-slate-700">{warrior.stats.countries}</p>
                        <p className="text-slate-500">Countries</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-3 text-slate-500">
                      {warrior.social.facebook ? (
                        <a href={warrior.social.facebook} aria-label={`${warrior.name} Facebook`}>
                          <Facebook size={16} />
                        </a>
                      ) : null}
                      {warrior.social.tiktok ? (
                        <a href={warrior.social.tiktok} aria-label={`${warrior.name} TikTok`}>
                          <Music2 size={16} />
                        </a>
                      ) : null}
                      {warrior.social.youtube ? (
                        <a href={warrior.social.youtube} aria-label={`${warrior.name} YouTube`}>
                          <Youtube size={16} />
                        </a>
                      ) : null}
                      {warrior.social.instagram ? (
                        <a href={warrior.social.instagram} aria-label={`${warrior.name} Instagram`}>
                          <Instagram size={16} />
                        </a>
                      ) : null}
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      <Link
                        to={warrior.to}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Watch story
                        <ArrowRight size={14} />
                      </Link>
                      <Link
                        to={`/donate?ambassador=${encodeURIComponent(warrior.slug)}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-[#1A5276] px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                      >
                        Donate through {warrior.name.split(' ')[0]}
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="text-3xl font-black text-[#1A5276]">{estimatedReach.toLocaleString()}</p>
              <p className="mt-1 text-sm text-slate-600">Estimated content views</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="text-3xl font-black text-[#148F77]">{totalVideos}</p>
              <p className="mt-1 text-sm text-slate-600">Ambassador videos</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="text-3xl font-black text-[#D4AC0D]">{maxLanguages}</p>
              <p className="mt-1 text-sm text-slate-600">Languages used</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
              <p className="text-3xl font-black text-[#1A5276]">{maxCountries}</p>
              <p className="mt-1 text-sm text-slate-600">Countries reached</p>
            </div>
          </div>

          <blockquote
            className="mt-8 rounded-xl border-l-4 border-[#148F77] bg-[#F8FAFC] p-5 text-lg italic text-slate-700"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            <p>
              “Every warrior story is a bridge between the reality of CKD families and the people
              willing to stand with them. That bridge saves lives.”
            </p>
            <span className="mt-3 block text-sm not-italic font-semibold text-slate-500">
              — GSGO Co-Director
            </span>
          </blockquote>
        </div>
      </section>

      <section id="apply" className="py-14 sm:py-20" style={{ backgroundColor: TEAL }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-white/85">
            WR-5 Become an Ambassador
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white">
            Apply to become a CKD Warrior Ambassador
          </h2>
          <p className="mt-4 text-white/90">
            We are looking for patients, survivors, and caregivers who want to help expand CKD
            awareness, fundraising, and community support.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-white/90">
            <li className="flex items-start gap-2">
              <Globe size={16} className="mt-0.5 shrink-0" />
              Share your CKD story and lived experience with compassion and accuracy.
            </li>
            <li className="flex items-start gap-2">
              <Globe size={16} className="mt-0.5 shrink-0" />
              Participate in campaign content and periodic ambassador activities.
            </li>
            <li className="flex items-start gap-2">
              <Globe size={16} className="mt-0.5 shrink-0" />
              Commit to ethical representation of patient and caregiver communities.
            </li>
          </ul>

          <div className="mt-8 rounded-2xl bg-white p-5 sm:p-7 shadow-[0_18px_40px_rgba(15,23,42,0.25)]">
            <AmbassadorApplicationForm source="warriors_apply" />
          </div>
        </div>
      </section>
    </main>
  );
}
